/* eslint-disable no-underscore-dangle */
import { normalizeAccountRole, selectUserRolePermissions } from '@socialive/common';
import PropTypes from 'prop-types';
import { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.minimal.css';
import ModalProxies from '~/components/ModalProxies';
import { isGuestInAllowedRoute, pathRequiresAuthentication } from '~/helpers/routerHelpers';
import Footer from '../components/Footer';
import GlobalHeader from '../components/GlobalHeader';
import GA from '../components/GoogleAnalytics';
import decodeURIComponentSafe from '../helpers/decodeURIComponentSafe';
import dynamicallySort from '../helpers/dynamicallySort';
import { Pathnames } from '../helpers/enums';
import isThisASuperAdmin from '../helpers/isThisASuperAdmin';
import { routerHistoryShape, routerLocationShape } from '../helpers/propTypeShapes';
import trackMixPanelEvent from '../helpers/trackMixPanelEvent';
import { getUserLastAccount, getUserLastChannelOnAccount, updateUserPreferences } from '../helpers/userPreferences';
import Routes from '../Routes';
import '../scripts/intercom-spa';
import { getBroadcasts, getChannels } from '../services';
import { selectApiBaseUrl, setAccountInfo, setAccountRole, setAuthToken, setCurrentAccountId } from '../store';
import '../style/app.css';
import '../style/global.css';
import '../style/icons.css';
import '../style/overrides.css';

if (typeof Intercom !== 'undefined') {
  window.Intercom('boot', {
    app_id: window.CONFIG.intercomAppId,
  });
}

class App extends Component {
  constructor(props) {
    super(props);
    // 8 days = 691200000, 2 days = 172800000

    const rawRole = sessionStorage.getItem('currentAccountRole') ?? localStorage.getItem('currentAccountRole');
    const globalAccountRole = normalizeAccountRole(rawRole);

    this.state = {
      globalAccountRole,
      amIASuperAdmin: false,
      APIBaseURL: props.apiBaseUrl,
      componentReady: false,
      currentYear: new Date().getFullYear(),
      globalAccountData: sessionStorage.getItem('globalAccountData')
        ? JSON.parse(sessionStorage.getItem('globalAccountData'))
        : {},
      globalAccountId: sessionStorage.getItem('currentAccountId')
        ? sessionStorage.getItem('currentAccountId')
        : localStorage.getItem('currentAccountId'),
      globalAccountName: sessionStorage.getItem('currentAccountName')
        ? sessionStorage.getItem('currentAccountName')
        : localStorage.getItem('currentAccountName'),
      globalUserData: sessionStorage.getItem('globalUserData')
        ? JSON.parse(sessionStorage.getItem('globalUserData'))
        : {},
      showHeader: true,
      channels: [],
      broadcastsData: { list: [], loading: false, error: false, noBroadcasts: false },
    };

    this.appLogin = this.appLogin.bind(this);
    this.authWebcaster = this.authWebcaster.bind(this);
    this.checkAuthorization = this.checkAuthorization.bind(this);
    this.handleIntercom = this.handleIntercom.bind(this);
    this.handleLogout = this.handleLogout.bind(this);
    this.handleMessageEvent = this.handleMessageEvent.bind(this);
    this.reAuth = this.reAuth.bind(this);
    this.redirectToLogin = this.redirectToLogin.bind(this);
    this.refreshAuthToken = this.refreshAuthToken.bind(this);
    this.showHeader = this.showHeader.bind(this);
    this.setChargebee = this.setChargebee.bind(this);
    this.setGlobalAccountData = this.setGlobalAccountData.bind(this);
    this.setGlobalAccountId = this.setGlobalAccountId.bind(this);
    this.setGlobalAccountName = this.setGlobalAccountName.bind(this);
    this.setGlobalAccountRole = this.setGlobalAccountRole.bind(this);
    this.setGlobalSuperAdminStatus = this.setGlobalSuperAdminStatus.bind(this);
    this.setGlobalUserData = this.setGlobalUserData.bind(this);
    this.setMobileDevice = this.setMobileDevice.bind(this);
    this.switchAccount = this.switchAccount.bind(this);
    this.trackEvent = this.trackEvent.bind(this);
    this.loadBroadcasts = this.loadBroadcasts.bind(this);
    this.loadChannels = this.loadChannels.bind(this);
    this.clearGlobalAccount = this.clearGlobalAccount.bind(this);
    this.handleBroadcastTitleUpdateFromStudio = this.handleBroadcastTitleUpdateFromStudio.bind(this);
    this.webcasterAuthResponseHandler = this.webcasterAuthResponseHandler.bind(this);
    this.fetchUser = this.fetchUser.bind(this);
  }

  componentDidMount() {
    const that = this;

    window.addEventListener('message', that.handleMessageEvent, false);

    this.setMobileDevice();
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.globalAccountId !== this.state.globalAccountId) {
      this.setChargebee();
    }

    // recalling on location to retrieve guest vs host broadcasts
    const pages = [Pathnames.DASHBOARD, Pathnames.JOIN, Pathnames.RECORDINGS];

    if (
      pages.includes(this.props.location.pathname) &&
      (this.props.location !== prevProps.location || prevState.globalAccountId !== this.state.globalAccountId)
    ) {
      this.loadChannels();
    }
  }

  componentWillUnmount() {
    const that = this;

    window.removeEventListener('message', that.handleMessageEvent, false);
  }

  handleMessageEvent(event) {
    if (event.data.message === 'basicAuth') {
      const iframe = document.getElementById('adminIframe').contentWindow;
      const data = {
        authToken: sessionStorage.getItem('authToken'),
        userId: sessionStorage.getItem('userId'),
        userName: sessionStorage.getItem('userName'),
        userEmail: sessionStorage.getItem('globalUserName'),
        currentAccountId: sessionStorage.getItem('currentAccountId'),
        currentChannelToken: sessionStorage.getItem('currentChannelToken'),
        currentBroadcastId: sessionStorage.getItem('currentBroadcastId'),
        currentBroadcastHash: sessionStorage.getItem('currentBroadcastHash'),
      };
      iframe.postMessage(
        {
          message: 'authBasic',
          value: data,
        },
        '*'
      );
    } else if (event.data.message === 'webcasterAuth') {
      this.authWebcaster();
    } else if (event.data.message === 'ssoCallbackSuccess') {
      window.SSOWindow.close();
      this.appLogin(JSON.parse(event.data.value), true);
    } else if (event.data.message === 'closed') {
      const closedBroadcastHash = event.data.value;
      const { broadcastsData } = this.state;
      const broadcastList = broadcastsData.list;
      const newBroadcastList = broadcastList.filter(({ hash }) => closedBroadcastHash !== hash);

      this.setState({
        broadcastsData: {
          ...broadcastsData,
          list: newBroadcastList,
        },
      });
    } else if (event.data.message === 'updateBroadcastTitle') {
      this.handleBroadcastTitleUpdateFromStudio(event.data.broadcastId, event.data.title);
    }
  }

  handleBroadcastTitleUpdateFromStudio(broadcastId, newTitle) {
    const { broadcastsData } = this.state;

    const updatedBroadcastList = broadcastsData.list.map((broadcast) => {
      if (broadcast.id === broadcastId) {
        return { ...broadcast, name: newTitle };
      }
      return broadcast;
    });

    this.setState({ broadcastsData: { ...broadcastsData, list: updatedBroadcastList } });
  }

  handleMixPanel() {
    if (window?.mixpanel) {
      const userId = sessionStorage.getItem('userId');

      if (userId === null) {
        return;
      }

      const userName = sessionStorage.getItem('userName') || '';
      const userEmail = sessionStorage.getItem('globalUserName') || '';
      const createdDate = JSON.parse(sessionStorage.getItem('globalUserData'))?.principal?.createdAt
        ? new Date(JSON.parse(sessionStorage.getItem('globalUserData'))?.principal?.createdAt).toISOString()
        : '';
      const rawRole = sessionStorage.getItem('currentAccountRole') || '';
      const accountRole = normalizeAccountRole(rawRole);
      const accountName = sessionStorage.getItem('currentAccountName') || '';
      const accountId = sessionStorage.getItem('currentAccountId') || '';

      window.mixpanel.identify(userId);
      window.mixpanel.register({
        $name: userName,
        $email: userEmail,
        'Account Role': accountRole,
        'Account Name': accountName,
        'Account ID': accountId,
      });
      window.mixpanel.people.set({
        $name: userName,
        $email: userEmail,
        $distinct_id: userId,
        $created: createdDate,
        'Account Role': accountRole,
        'Account Name': accountName,
        'Account ID': accountId,
      });
      window.mixpanel.add_group('Account ID', accountId);
      window.mixpanel.get_group('Account ID', accountId).set_once({ $name: accountName });
    }
  }

  handleIntercom() {
    const { appId } = window;
    const data = this.state.globalUserData;
    const accountData = this.state.globalAccountData;

    if (data !== null) {
      if (Object.keys(data).length > 0) {
        window.Intercom('update', {
          app_id: appId,
          // custom_launcher_selector: '#btnMessenger',	//custom button id
          hide_default_launcher: false,
          email: data.principal.email,
          user_id: data.principal.id,
          signed_up_at: Math.round(new Date(data.principal.createdAt).getTime() / 1000),
          name: data.principal.name,
          location: data.principal.location,
          company: {
            id: this.state.globalAccountId,
            name: sessionStorage.getItem('currentAccountName'),
            created_at: Math.round(new Date(accountData._embedded?.accounts[0].createdAt).getTime() / 1000),
          },
          'Account Name': this.state.globalAccountName,
          'Account ID': this.state.globalAccountId,
          'Account Role': this.state.globalAccountRole,
        });
      }
    }
  }

  handleDataServices() {
    this.handleIntercom();
    this.handleMixPanel();
  }

  handleLogout() {
    const params = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-VERSION': '2.0',
        authorization: sessionStorage.getItem('authToken'),
      },
    };
    this.trackEvent('Portal Logout');
    const updatedUserPreferences = updateUserPreferences();

    sessionStorage.clear();
    localStorage.clear();

    if (updatedUserPreferences) {
      localStorage.setItem('usersPreferences', updatedUserPreferences);
    }

    fetch(`${this.state.APIBaseURL}/auth/logout`, params)
      .then(() => {
        this.props.history.push('/login');
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('auth/logout API call failed. ', error);
        this.props.history.push('/login');
      });
  }

  getCookieValue(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);

    if (parts.length === 2) {
      return parts.pop().split(';').shift();
    }
    return undefined;
  }

  getPrimaryKey(hrefObj) {
    const source = hrefObj.href.split('/');
    const primaryKey = source[source.length - 1];

    return primaryKey;
  }

  getScreenOrientation() {
    let orientation;

    if (window.screen.orientation) {
      switch (window.screen.orientation) {
        case 'portrait-primary':
          orientation = 0;
          break;
        case 'portrait-secondary':
          orientation = 180;
          break;
        case 'landscape-primary':
          orientation = 90;
          break;
        case 'landscape-secondary':
          orientation = -90;
          break;
        default:
          orientation = null;
      }
    } else {
      orientation = window.orientation;
    }
    return orientation;
  }

  setMobileDevice() {
    const isMobile = this.mobileCheck();

    sessionStorage.setItem('isMobileSession', isMobile);
    if (isMobile && window.Intercom) {
      window.Intercom('update', {
        hide_default_launcher: true,
      });
    }
  }

  setChargebee() {
    let currentAccount;

    if (this.state.globalAccountData._embedded) {
      currentAccount = this.state.globalAccountData._embedded.accounts.find(
        (x) => x.id === parseInt(this.state.globalAccountId, 10)
      );

      if (currentAccount.provider === 'chargebee') {
        sessionStorage.setItem('isChargebeeSession', true);
        sessionStorage.setItem('subscriberId', currentAccount.subscriberId);
        localStorage.setItem('isChargebeeSession', true);
        localStorage.setItem('subscriberId', currentAccount.subscriberId);
      } else {
        sessionStorage.setItem('isChargebeeSession', false);
        localStorage.setItem('isChargebeeSession', false);
        sessionStorage.removeItem('subscriberId');
      }
    }
  }

  setGlobalAccountId(id, isInitial, role) {
    sessionStorage.setItem('currentAccountId', id);
    localStorage.setItem('currentAccountId', id);
    if (this.state.globalAccountId !== id) {
      this.setState({ globalAccountId: id }, () => {
        if (!isInitial) {
          this.trackEvent('Account switched', {
            accountId: id,
            role,
          });
          this.handleDataServices();
        }
      });
    }
  }

  setGlobalAccountData(data) {
    this.setState({ globalAccountData: data }, () => {
      sessionStorage.setItem('globalAccountData', JSON.stringify(data));
    });
  }

  setGlobalAccountName(name) {
    this.setState({ globalAccountName: name });
    sessionStorage.setItem('currentAccountName', name);
    localStorage.setItem('currentAccountName', name);
  }

  setGlobalAccountRole(role) {
    const parsedRole = normalizeAccountRole(role);
    this.setState({ globalAccountRole: parsedRole });
    sessionStorage.setItem('currentAccountRole', parsedRole);
    localStorage.setItem('currentAccountRole', parsedRole);
    this.props.setAccountRole({ role: parsedRole, trigger: 'App@setGlobalAccountRole' });
  }

  setGlobalUserData(data) {
    this.setState(
      {
        globalUserData: data,
      },
      () => {
        localStorage.setItem('globalUserData', JSON.stringify(data));
        sessionStorage.setItem('globalUserData', JSON.stringify(data));
      }
    );
  }

  setGlobalSuperAdminStatus() {
    const userName = sessionStorage.getItem('globalUserName');
    if (userName === null) {
      this.setState({ amIASuperAdmin: false });
      return;
    }

    this.setState({ amIASuperAdmin: isThisASuperAdmin(userName) });
  }

  mobileCheck() {
    let check = false;
    ((a) => {
      if (
        /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(
          a
        ) ||
        /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw-(n|u)|c55\/|capi|ccwa|cdm-|cell|chtm|cldc|cmd-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc-s|devi|dica|dmob|do(c|p)o|ds(12|-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(-|_)|g1 u|g560|gene|gf-5|g-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd-(m|p|t)|hei-|hi(pt|ta)|hp( i|ip)|hs-c|ht(c(-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i-(20|go|ma)|i230|iac( |-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|-[a-w])|libw|lynx|m1-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|-([1-8]|c))|phil|pire|pl(ay|uc)|pn-2|po(ck|rt|se)|prox|psio|pt-g|qa-a|qc(07|12|21|32|60|-[2-7]|i-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h-|oo|p-)|sdk\/|se(c(-|0|1)|47|mc|nd|ri)|sgh-|shar|sie(-|m)|sk-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h-|v-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl-|tdg-|tel(i|m)|tim-|t-mo|to(pl|sh)|ts(70|m-|m3|m5)|tx-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas-|your|zeto|zte-/i.test(
          a.substr(0, 4)
        )
      )
        check = true;
    })(navigator.userAgent || navigator.vendor || window.opera);
    return check;
  }

  async webcasterAuthResponseHandler(response) {
    if (response.status >= 400) {
      // eslint-disable-next-line no-console
      console.error(`Received ${response.status} response from refreshAuthToken. Token expired, redirecting to login.`);
      window.webcasterWindow.close();
      this.reAuth(this.props.location.pathname, encodeURIComponent(this.props.location.search));
      return undefined;
    }
    return response.json();
  }

  refreshAuthToken(returnTokenToStudio = false) {
    const params = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        authorization: sessionStorage.getItem('authToken'),
      },
    };

    fetch(`${this.state.APIBaseURL}/auth/refresh`, params)
      .then(this.webcasterAuthResponseHandler)
      .then(({ authToken }) => {
        const newExpiry = Date.now() + 604800000;
        const payload = {
          authToken,
          expiry: String(newExpiry),
        };

        this.props.setAuthToken({ authToken, trigger: 'App@refreshAuthToken' });
        sessionStorage.setItem('authToken', authToken);
        sessionStorage.setItem('expiry', newExpiry);
        localStorage.setItem('authToken', authToken);
        localStorage.setItem('expiry', newExpiry);
        if (returnTokenToStudio) {
          this.studioPostMessage(payload);
        }
        this.handleMixPanel();
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('refreshAuthToken API call failed. ', { error });
      });
  }

  reAuth(referrer, query = '') {
    const decodedQuery = decodeURIComponentSafe(query);
    const urlParams = new URLSearchParams(decodedQuery);

    const referrerOverride = urlParams.get('referrer') ?? referrer;
    urlParams.delete('referrer');
    const params = urlParams.toString();

    const urlQuery = params.length > 0 ? `?${params}` : '';

    if (!document.location.href.includes('webview')) {
      const updatedUserPreferences = updateUserPreferences();

      sessionStorage.clear();
      localStorage.clear();

      if (updatedUserPreferences) {
        localStorage.setItem('usersPreferences', updatedUserPreferences);
      }

      this.redirectToLogin(`${referrerOverride}${urlQuery}`);
    } else {
      try {
        window.webkit.messageHandlers.authFailedEvent.postMessage('authFailedEvent');
      } catch {
        toast.error('Authorization Failed.');
      }
    }
  }

  redirectToLogin(referrer) {
    this.props.history.push(`/login?referrer=${referrer}`);
  }

  checkAuthorization(referrer, query = '') {
    const localExpiry = localStorage.getItem('expiry');
    const sessionExpiry = sessionStorage.getItem('expiry');
    const { pathname } = this.props.location;
    const { canViewOwnRecordings } = this.props.rolePermissions;
    const decodedQuery = decodeURIComponentSafe(query);
    const authToken = localStorage.getItem('authToken');
    this.props.setAuthToken({ authToken, trigger: 'App@checkAuthorization' });
    const now = Date.now();

    this.setGlobalSuperAdminStatus();

    if (authToken && !canViewOwnRecordings && !isGuestInAllowedRoute(referrer)) {
      this.props.history.push('/dashboard');
      return false;
    }

    if (pathRequiresAuthentication(pathname)) {
      if (localExpiry === null && sessionExpiry === null) {
        this.redirectToLogin(referrer + decodedQuery);
        return false;
      }
      if (sessionExpiry && +sessionExpiry < now) {
        this.redirectToLogin(referrer + decodedQuery);
        return false;
      }
      if (sessionExpiry === null && +localExpiry < now) {
        this.redirectToLogin(referrer + decodedQuery);
        return false;
      }

      if (sessionExpiry === null && +localExpiry > now) {
        this.copyLocalToSession();
        this.handleDataServices();
        this.setState({ componentReady: true });
      } else {
        this.handleDataServices();
        this.setState({ componentReady: true });
      }
      return true;
    }

    if (+localExpiry > now) {
      this.copyLocalToSession();
      this.handleDataServices();
      this.props.history.push(`/dashboard${decodedQuery}`);
      return false;
    }

    return true;
  }

  copyLocalToSession() {
    const localExpiry = localStorage.getItem('expiry');

    sessionStorage.setItem('accounts', localStorage.getItem('accounts'));
    sessionStorage.setItem('authToken', localStorage.getItem('authToken'));
    sessionStorage.setItem('createBroadcastChannel', localStorage.getItem('createBroadcastChannel'));
    sessionStorage.setItem('currentAccountId', localStorage.getItem('currentAccountId'));
    sessionStorage.setItem('currentAccountName', localStorage.getItem('currentAccountName'));
    sessionStorage.setItem('currentAccountRole', normalizeAccountRole(localStorage.getItem('currentAccountRole')));
    sessionStorage.setItem('isChargebeeSession', localStorage.getItem('isChargebeeSession'));
    sessionStorage.setItem('subscriberId', localStorage.getItem('subscriberId'));
    sessionStorage.setItem('expiry', localExpiry);
    if (localStorage.getItem('filterByChannel') !== null) {
      sessionStorage.setItem('filterByChannel', localStorage.getItem('filterByChannel'));
    } else {
      sessionStorage.setItem('filterByChannel', 'allChannels');
    }
    if (localStorage.getItem('filterByChannelPB') !== null) {
      sessionStorage.setItem('filterByChannelPB', localStorage.getItem('filterByChannelPB'));
    } else {
      sessionStorage.setItem('filterByChannelPB', 'allChannels');
    }

    sessionStorage.setItem('globalUserName', localStorage.getItem('globalUserName'));
    sessionStorage.setItem('userId', localStorage.getItem('userId') ?? '');
    sessionStorage.setItem('userName', localStorage.getItem('userName'));
    if (localStorage.getItem('globalUserData') !== null) {
      sessionStorage.setItem('globalUserData', localStorage.getItem('globalUserData'));
    }
  }

  async appLogin(data, keepLoggedIn = false, redirectURL = null, externalAccountId = null) {
    // Add 7 days to current time for expiration date
    const expiry = Date.now() + 604800000;
    let selectedAccount;
    const { id: userId, name: userName, email } = data.principal;

    const accountParams = {
      headers: {
        authorization: data.authToken,
      },
    };

    if (Object.keys(data).length > 0) {
      this.setGlobalUserData(data);
    }

    this.props.setAuthToken({ authToken: data.authToken, trigger: 'App@appLogin' });
    if (keepLoggedIn) {
      localStorage.setItem('authToken', data.authToken);
      localStorage.setItem('expiry', expiry);
      localStorage.setItem('userId', userId);
      localStorage.setItem('userName', userName);
      localStorage.setItem('globalUserName', email);
    }
    sessionStorage.setItem('authToken', data.authToken);
    sessionStorage.setItem('expiry', expiry);
    sessionStorage.setItem('userId', userId);
    sessionStorage.setItem('userName', userName);
    sessionStorage.setItem('globalUserName', email);

    const lastAccount = getUserLastAccount();

    return fetch(`${this.state.APIBaseURL}/accounts/search/my-access?projection=detail`, accountParams)
      .then((response) => response.json())
      .then((responseData) => {
        const searchParams = new URLSearchParams(this.props.location.search);
        let accountId = null;
        let accountIndex = '';

        if (!(responseData && responseData._embedded && responseData._embedded.accounts)) {
          throw new Error('Account data not provided.');
        }

        const { accounts } = responseData._embedded;

        if (externalAccountId) {
          accountId = parseInt(externalAccountId, 10);
        } else if (searchParams.get('account_id') !== null) {
          accountId = parseInt(searchParams.get('account_id'), 10);
        } else if (lastAccount) {
          accountId = parseInt(lastAccount, 10);
        }
        if (accountId) {
          for (let i = 0; i < accounts.length; i += 1) {
            if (accounts[i].id === accountId) {
              accountIndex = i;
            }
          }
          if (accountIndex !== '') {
            selectedAccount = accounts[accountIndex];

            this.setGlobalAccountData(responseData);
            this.setGlobalAccountId(selectedAccount.id, true, selectedAccount.accountUsers.role);
            this.setGlobalAccountName(selectedAccount.name);
            this.setGlobalAccountRole(selectedAccount.accountUsers.role);
          }
        } else {
          [selectedAccount] = accounts;

          this.setGlobalAccountData(responseData);
          this.setGlobalAccountId(selectedAccount.id, true, selectedAccount.accountUsers.role);
          this.setGlobalAccountName(selectedAccount.name);
          this.setGlobalAccountRole(selectedAccount.accountUsers.role);
        }
        if (responseData && responseData._embedded && responseData._embedded.accounts) {
          selectedAccount = selectedAccount ?? accounts[0];
          sessionStorage.setItem('accounts', JSON.stringify(responseData._embedded.accounts));
          localStorage.setItem('accounts', JSON.stringify(responseData._embedded.accounts));
          this.props.setAccountInfo({
            accountId: selectedAccount.id,
            accounts,
            userId,
            userName,
            email,
            role: normalizeAccountRole(selectedAccount?.accountUsers?.role),
            trigger: 'App@appLogin',
          });
        }
      })
      .then(() => {
        this.handleDataServices();
        trackMixPanelEvent('Login');

        if (this.props.location.search.length > 0 && redirectURL === null) {
          const safeSearchString = decodeURIComponentSafe(this.props.location.search);
          const searchParams = new URLSearchParams(safeSearchString);
          const referrerRedirectPath = searchParams.get('referrer');
          // depending on what triggered app.reAuth the referrer path maybe just "/"
          // if the path is just "/" then the user just sits on the login page and nothing appears to have happened
          // though in fact things have happened.
          if (referrerRedirectPath !== null && referrerRedirectPath !== '/') {
            this.trackEvent('Login', {
              role: selectedAccount.accountUsers.role,
            });
            this.props.history.push(referrerRedirectPath);
          } else {
            this.trackEvent('Login', {
              role: selectedAccount.accountUsers.role,
            });
            this.props.history.push('/dashboard');
          }
        } else if (redirectURL !== null) {
          this.trackEvent('Login', {
            role: selectedAccount.accountUsers.role,
          });
          this.props.history.push(redirectURL);
        } else {
          this.trackEvent('Login', {
            role: selectedAccount.accountUsers.role,
          });
          this.props.history.push('/dashboard');
        }
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('my-access API call failed. ', error);
      });
  }

  clearGlobalAccount() {
    return new Promise((resolve) =>
      this.setState(
        {
          globalAccountData: {},
          globalAccountId: '',
          globalAccountName: '',
          globalAccountRole: '',
        },
        resolve
      )
    );
  }

  async fetchUser(authToken) {
    const params = {
      method: 'post',
      headers: {
        'Content-Type': 'application/json',
        'X-API-VERSION': '2.0',
        authorization: authToken,
      },
    };
    return fetch(`${this.state.APIBaseURL}/auth/me`, params).then(this.webcasterAuthResponseHandler);
  }

  async authWebcaster() {
    const data = {
      authToken: sessionStorage.getItem('authToken'),
      expiry: sessionStorage.getItem('expiry'),
    };
    const storedExpiry = localStorage.getItem('expiry');
    const now = Date.now();
    const timeToExpiry = storedExpiry - now;
    const tokenIsFresh = timeToExpiry > 172800000; // 2 days = 172800000

    if (!tokenIsFresh) {
      this.refreshAuthToken(true);
      return;
    }

    // Check token validity by requesting user data
    const user = await this.fetchUser(data.authToken);
    if (user?.id) {
      this.studioPostMessage(data);
    }
  }

  studioPostMessage(data) {
    if (window.webcasterWindow) {
      window.webcasterWindow.postMessage(
        {
          message: 'webcasterAuthFromPortal',
          value: data,
        },
        '*'
      );
    }
  }

  showHeader(bool) {
    this.setState({ showHeader: bool });
  }

  throttle(callback, wait, immediate = false) {
    let timeout = null;
    let initialCall = true;

    return (...args) => {
      const callNow = immediate && initialCall;
      const next = () => {
        callback.apply(this, args);
        timeout = null;
      };
      if (callNow) {
        initialCall = false;
        next();
      }
      if (!timeout) {
        timeout = setTimeout(next, wait);
      }
    };
  }

  trackEvent(eventName, metadata) {
    if (metadata) {
      window.Intercom('trackEvent', eventName, metadata);
    } else {
      window.Intercom('trackEvent', eventName);
    }
  }

  loadBroadcasts() {
    const userId = sessionStorage.getItem('userId');
    this.setState({
      broadcastsData: { list: [], loading: true, error: false, noBroadcasts: false },
    });
    if (!userId) {
      this.reAuth(this.props.location.pathname);
      return Promise.resolve();
    }

    return getBroadcasts(
      this.state.globalAccountId,
      this.state.globalAccountRole,
      this.state.APIBaseURL,
      this.reAuth,
      this.trackEvent,
      userId
    ).then((data) => {
      this.setState({
        broadcastsData: data,
      });
    });
  }

  loadChannels() {
    if (document.location.href.includes('webview') || !this.state.globalAccountId) {
      return;
    }

    getChannels(this.state.globalAccountId, this.state.APIBaseURL, dynamicallySort, this.reAuth).then((data) => {
      this.setState({ channels: data });

      const currentChannel = getUserLastChannelOnAccount();

      const createBroadcastChannel = sessionStorage.getItem('createBroadcastChannel');
      if (!createBroadcastChannel) {
        if (currentChannel && data?.find(({ id }) => id === currentChannel)) {
          sessionStorage.setItem('createBroadcastChannel', currentChannel);
          localStorage.setItem('createBroadcastChannel', currentChannel);
        } else {
          sessionStorage.setItem('createBroadcastChannel', data?.[0]?.id);
          localStorage.setItem('createBroadcastChannel', data?.[0]?.id);
        }
      }
    });
  }

  switchAccount({ accountId, accountName, role, pathNames = [] }) {
    const { pathname } = this.props.location;
    const { canNavigateInPortal } = this.props.rolePermissions;
    localStorage.removeItem('createBroadcastChannel');
    sessionStorage.removeItem('createBroadcastChannel');

    trackMixPanelEvent('Switch account', {
      'Account Name': accountName,
      'Account ID': accountId,
      'Account Role': role,
    });

    const pagesToStay = [
      Pathnames.DASHBOARD,
      Pathnames.JOIN,
      Pathnames.RECORDINGS,
      Pathnames.USERS,
      Pathnames.CHANNELS,
      Pathnames.PLAYERS,
      Pathnames.CONNECTION_MANAGER,
      ...pathNames,
    ];

    this.props.setCurrentAccountId(accountId);
    this.setGlobalAccountId(accountId, false, role);
    this.setGlobalAccountName(accountName);
    this.setGlobalAccountRole(role);
    if (!pagesToStay.includes(pathname) || !canNavigateInPortal) {
      this.props.history.push('/dashboard');
    }
  }

  render() {
    let className = 'app container';
    let header = '';
    let footer = '';

    const { showHeader, globalAccountId, globalAccountRole } = this.state;

    if (showHeader && globalAccountId && globalAccountRole) {
      header = (
        <GlobalHeader
          loadBroadcasts={this.loadBroadcasts}
          reAuth={this.reAuth}
          setChargebee={this.setChargebee}
          switchAccount={this.switchAccount}
          trackEvent={this.trackEvent}
        />
      );
      footer = <Footer />;
    } else {
      className += ' no-header';
    }
    return (
      <div className={className}>
        <ToastContainer className="toast-container" />
        {GA.init() && <GA.RouteTracker />}
        {header}
        <Routes
          APIBaseURL={this.state.APIBaseURL}
          appLogin={this.appLogin}
          accountId={this.state.globalAccountId}
          accountRole={this.state.globalAccountRole}
          amIASuperAdmin={this.state.amIASuperAdmin}
          broadcastsData={this.state.broadcastsData}
          clearGlobalAccount={this.clearGlobalAccount}
          channels={this.state.channels}
          checkAuthorization={this.checkAuthorization}
          componentReady={this.state.componentReady}
          currentYear={this.state.currentYear}
          getCookieValue={this.getCookieValue}
          getPrimaryKey={this.getPrimaryKey}
          getScreenOrientation={this.getScreenOrientation}
          handleLogout={this.handleLogout}
          loadBroadcasts={this.loadBroadcasts}
          reAuth={this.reAuth}
          setMobileDevice={this.setMobileDevice}
          setGlobalAccountData={this.setGlobalAccountData}
          setGlobalAccountId={this.setGlobalAccountId}
          setGlobalAccountName={this.setGlobalAccountName}
          setGlobalAccountRole={this.setGlobalAccountRole}
          setGlobalUserData={this.setGlobalUserData}
          showHeader={this.showHeader}
          switchAccount={this.switchAccount}
          throttle={this.throttle}
          trackEvent={this.trackEvent}
          loadChannels={this.loadChannels}
        />
        {footer}
        <ModalProxies reAuth={this.reAuth} loadBroadcasts={this.loadBroadcasts} trackEvent={this.trackEvent} />
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  apiBaseUrl: selectApiBaseUrl(state),
  rolePermissions: selectUserRolePermissions(state),
});
const mapDispatchToProps = { setAccountInfo, setAuthToken, setAccountRole, setCurrentAccountId };

App.propTypes = {
  history: routerHistoryShape.isRequired,
  location: routerLocationShape.isRequired,
  setAccountInfo: PropTypes.func.isRequired,
  setAccountRole: PropTypes.func.isRequired,
  setAuthToken: PropTypes.func.isRequired,
  apiBaseUrl: PropTypes.string.isRequired,
  setCurrentAccountId: PropTypes.func.isRequired,
  rolePermissions: PropTypes.shape({
    canNavigateInPortal: PropTypes.bool,
    canViewOwnRecordings: PropTypes.bool,
  }).isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(App));
